import {
  Tab as MuiTab,
  Tabs as MuiTabs,
  TabsProps as MuiTabsProps,
  TabProps,
  useTheme,
} from '@mui/material';
import React from 'react';
import { cn } from '../utils';

interface TabsProps extends MuiTabsProps {
  size?: 'small' | 'medium';
  type?: 'default' | 'buttonGroup';
}

export function Tabs({
  children,
  value,
  className,
  sx,
  size = 'medium',
  type = 'default',
  ...props
}: TabsProps) {
  const theme = useTheme();
  const tabCount = React.Children.count(children);
  return (
    <div
      className={cn(
        className,
        'flex flex-row bg-gray-100 rounded-md h-[44px] border border-gray-200',
        size === 'small' ? 'h-[36px]' : 'h-[44px]',
      )}
    >
      <MuiTabs
        value={value}
        {...props}
        sx={{
          ...sx,
          minHeight: 0,
          height: '100%',
          ...(type === 'default' && { py: '1px', px: '1px' }),
          width: '100%',
          display: 'flex',
          border: 0,
          '& .MuiTabs-flexContainer': {
            minHeight: 'unset',
            height: '100%',
            justifyContent: 'space-around',
            gap: type === 'default' ? '4px' : '0px',
          },
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTabs-indicatorSpan': {
            height: '100%',
            width: '100%',
            backgroundColor: theme.palette.neutral.white,
          },
        }}
      >
        {React.Children.map(children, (child, index) =>
          React.cloneElement(child as React.ReactElement, {
            tabCount,
            tabIndex: index,
            type,
          }),
        )}
      </MuiTabs>
    </div>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
  tab: string;
  value: string;
  className?: string;
}

export function TabPanel({
  children,
  tab,
  value,
  className,
  ...props
}: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== tab}
      id={`simple-tabpanel-${tab}`}
      aria-labelledby={`simple-tab-${tab}`}
      className={cn(className, value !== tab ? 'hidden' : undefined)}
      {...props}
    >
      {value === tab && children}
    </div>
  );
}

interface TabTriggerProps extends TabProps {
  tabCount?: number;
  tabIndex?: number;
  type?: 'default' | 'buttonGroup';
}

function a11yProps(index: string) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

export function TabTrigger({
  tabCount,
  value,
  tabIndex,
  type,
  ...props
}: TabTriggerProps) {
  const {
    palette: { greyscaleLight },
  } = useTheme();
  const width = tabCount
    ? `calc(${100 / tabCount}% - ${type === 'buttonGroup' ? '0px' : '3px'})`
    : 'auto';
  if (!tabCount) {
    return null;
  }
  const isFirst = tabIndex === 0;
  const isLast = tabIndex === tabCount - 1;
  const isMiddle = !isFirst && !isLast;

  return (
    <MuiTab
      disableRipple
      value={value}
      {...a11yProps(value)}
      {...props}
      sx={{
        minHeight: 'unset',
        height: '100%',
        padding: 0,
        width,
        ...(type === 'buttonGroup' && isFirst && {
          borderTopLeftRadius: '4px',
          borderBottomLeftRadius: '4px',
          borderRight: `1px solid ${greyscaleLight[200]}`,
        }),
        ...(type === 'buttonGroup' && isLast && {
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px',
          borderLeft: `1px solid ${greyscaleLight[200]}`,
        }),
        ...(type === 'buttonGroup' && {
          borderRight: `1px solid ${greyscaleLight[200]}`,
        }),
        ...(type === 'default' && {
          borderRadius: '4px',
        }),
        fontSize: '14px',
        fontWeight: '600',
        '&.Mui-selected': {
          minHeight: 'unset',
          height: '100%',
          bgcolor: '#fff',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: greyscaleLight[200],
          boxShadow:
            '0px 2px 4px -10px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.04)',
        },
        ...(props.sx || {}),
      }}
    />
  );
}
